import { ActionContext, Module } from 'vuex';
import { RootStoreState } from '@/store';
import { ApiService } from '@libs-uuif/shared/api-service';
import { currentUserKey } from '@/shared/msw-constants';
import { envCongif } from '@/shared/env.config';
import * as decode from 'jwt-decode';
import { TableEntry } from '@sonicwall/unified-ui-framework/src/shared/sw-table';

export interface ConnectWiseManageStoreState {
  cwServerDetailsTemp: any;
  cwServerDetails: any;
  cwSyncDetails: any;
  cwTicketConfigs: any;
  cwTicketConfigsTemp: any;
  employees: object[];
  serviceBoard: object[];
  priorityOptions: object[];
  submitted: boolean;
  modalClosed: boolean;
  tenantsData: any;
  tempTenantsData: any;
  showCustomCatalogMapping: boolean;
  cwProductBinding: TableEntry[];
  isCustomCatalogStepValidated: boolean;
}

const getDefaultState = () => {
  return {
    cwServerDetails: {
      id: '',
      orgId: '',
      serverUrl: '',
      companyId: '',
      apiPrivateKey: '',
      apiPublicKey: '',
      userList: [],
    },
    cwServerDetailsTemp: {},
    cwTicketConfigs: {
      id: '',
      serviceBoard: '',
      ccCritical: '',
      ccHigh: '',
    },
    cwTicketConfigsTemp: {},
    cwSyncDetails: {
      id: '',
      server_url: '',
      company_id: '',
      last_sync: '',
      last_sync_wo_errors: '',
      last_sync_details: [],
      service_board: '',
    },
    serviceBoard: [],
    priorityOptions: [],
    submitted: false,
    employees: [],
    tenantsData: [],
    tempTenantsData: [],
    modalClosed: false, // For calling refresh data if config stored successfully
    showCustomCatalogMapping: false,
    cwProductBinding: [],
    isCustomCatalogStepValidated: true,
  };
};

const apiObject = new ApiService(`${envCongif.thirtParty_url}`);
// const postIntegrationConfig = '/save_integration_config';
// const checkIntegrationConfig = '/check_integration_config';
export default {
  state: getDefaultState,
  mutations: {
    setCwServerDetails(state, payload) {
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      payload['id'] = id;
      state.cwServerDetailsTemp = Object.assign({}, payload);
      state.cwServerDetails = Object.assign({}, payload);
    },
    setSyncDetails(state, payload) {
      state.cwSyncDetails = payload;
    },
    updateCwServerDetails(state, updatedObj) {
      state.cwServerDetails[updatedObj.key] = updatedObj.value;
    },
    updateEmployees(state, employees = []) {
      state.employees = JSON.parse(JSON.stringify(employees)); // to set the employees
    },
    updateSubmitted(state, submitted) {
      state.submitted = submitted;
    },
    updateServiceBoard(state, serviceBoard) {
      state.serviceBoard = serviceBoard;
    },
    updatePriorityOptions(state, priorityOptions) {
      state.priorityOptions = priorityOptions;
    },
    setTenantsData(state, payload) {
      state.tenantsData = payload;
    },
    setTempTenantsData(state, payload) {
      state.tempTenantsData = payload;
    },
    saveTicketConfiguration(state, ticketConfig) {
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      ticketConfig['id'] = id;
      console.warn(ticketConfig['id']);
      state.cwTicketConfigsTemp = Object.assign({}, ticketConfig);
      state.cwTicketConfigs = ticketConfig;
    },
    updateTicketConfiguration(state, updatedObj) {
      state.cwTicketConfigs[updatedObj.key] = updatedObj.value;
    },
    resetState (state) {
      // Merge rather than replace so we don't lose observers
      // https://github.com/vuejs/vuex/issues/1118
      Object.assign(state, getDefaultState());
    },
    setShowCustomCatalogMapping(state, enabled) {
      state.showCustomCatalogMapping = enabled;
    },
    updateCwProductBindings(state, payload) {
      state.cwProductBinding = payload;
    },
    updateIsCustomCatalogStepValidated(state, payload) {
      state.isCustomCatalogStepValidated = payload;
    },
  },
  actions: {
    // Configuration of Autotask api's
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchCwIntegrationDetails(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getAutotaskServerDetails = `/get_integration_config/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getAutotaskServerDetails);
    },
    getServiceBoard(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getServiceBoard = `/get_board_list/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getServiceBoard);
    },
    getPriorityOptions(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getPriorityOptions = `/get_priority_list/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getPriorityOptions);
    },
    getTicketConfig(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getTicketConfiguration = `/get_cw_ticket_config/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getTicketConfiguration);
    },
    saveCwIntegrationDetails(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const getAutotaskServerDetails = '/save_integration_config';
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.post(getAutotaskServerDetails, JSON.stringify({ integration_config: context.state.cwServerDetails, ticket_config: context.state.cwTicketConfigs, syncNow: true }));
    },
    checkIntegrationConfig(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const decodedToken: any = decode(JSON.parse(localStorage.getItem(currentUserKey) as any).token);
      context.state.cwServerDetails.id = id;
      context.state.cwServerDetails.orgId = decodedToken?.orgid || '';
      const checkForIntegrationConfig = '/check_integration_config';
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.post(checkForIntegrationConfig, context.state.cwServerDetails);
    },
    // to get last sync details
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    /* tslint:disable-next-line */
    getSyncDetails(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>, refresh: boolean) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      let getSyncDetails = `/get_sync_details/${id}?mode=new`;
      if (refresh) {
        getSyncDetails += '&isForced=true';
      }
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getSyncDetails);
    },
    // For table data update
    postTenantDetails(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>, tenantData) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const postTenantData = `/save_sync_details/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.post(postTenantData, JSON.stringify(tenantData));
    },
    // called the below two api on sync now
    getSyncJobStatus(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getJobStatus = `/get_job_state/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getJobStatus);
    },
    startSyncNow(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getSyncNowStatus = `/sync_now/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.post(getSyncNowStatus, {});
    },
    // get copmpany list
    // tslint:disable-next-line:no-unused
    getCompanyList(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>, payload) {
      // context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getCompanyList = `/get_company_list/${id}?isForced=${payload.isForced}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getCompanyList);
    },
    // Tenant's sync staus( Used along with table )
    getTenantSyncStatus(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const getTenantSyncStatus = `/get_tenant_sync_status/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getTenantSyncStatus);
    },
    setSyncDetailsToStore({ commit }, payload) {
      commit('setSyncDetails', payload);
    },
    resetConfigAndSync(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const resetConfigSync = `/reset_sync_config/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.delete(resetConfigSync);
    },
    getPSAVersion(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const getVersion = '/version?mode=new';
      // apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.get(getVersion);
    },
    getProductCatalogs(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>) {
      context.commit('startLoading', null, { root: true });
      const reqBody:any = {
        companyId: context.state.cwServerDetails?.companyId || '',
        publicKey: context.state.cwServerDetails?.apiPublicKey || '',
        privateKey: context.state.cwServerDetails?.apiPrivateKey || '',
        serverUrl: context.state.cwServerDetails?.serverUrl || '',
      };
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const productCatalogsApiURL:string = `/custom_catalog_mappings/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.post(productCatalogsApiURL, reqBody);
    },
    addProductCatalogs(context: ActionContext<ConnectWiseManageStoreState, RootStoreState>, payload) {
      context.commit('startLoading', null, { root: true });
      const reqBody:any = {
        companyId: context.state.cwServerDetails?.companyId || '',
        mappings: payload,
      };
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      const productCatalogsApiURL:string = `/custom_catalog_mappings/${id}`;
      apiObject.setAuth({ type: 'auth-token', authToken: JSON.parse(localStorage.getItem(currentUserKey) as any).token });
      return apiObject.put(productCatalogsApiURL, reqBody);
    },
  },
  getters: {
    getCwServerDetails: state => state.cwServerDetails,
    getCwServerDetailsTemp: state => state.cwServerDetailsTemp,
    getSyncDetails: state => state.cwSyncDetails,
    getEmployees: state => state.employees,
    getSubmitted: state => state.submitted,
    getModalClosed: state => state.modalClosed,
    getTenantsData: state => state.tenantsData,
    getTempTenantsData: state => state.tempTenantsData,
    getPriorityOptions: state => state.priorityOptions,
    getServiceBoard: state => state.serviceBoard,
    getTicketConfigurations: state => state.cwTicketConfigs,
    getTempTicketConfigurations: state => state.cwTicketConfigsTemp,
    getShowCustomCatalogMapping: state => state.showCustomCatalogMapping,
    getCwProductBinding: state => state.cwProductBinding,
    getIsCustomCatalogStepValidated: state => state.isCustomCatalogStepValidated,
    getErrorLogsLink: () => {
      const BASEURL = envCongif.thirtParty_url;
      const currentUser = JSON.parse(localStorage.getItem(currentUserKey) as any);
      const token = currentUser.token;
      const id = JSON.parse(localStorage.getItem(currentUserKey) as any).userId;
      // state.errorLogsLink = `${BASEURL}/get_error_logs/${id}?Authorization=${token}`;
      return `${BASEURL}/get_error_logs/${id}?Authorization=${token}`;
    },
  },
  namespaced: true,
} as Module<ConnectWiseManageStoreState, RootStoreState>;
